import type { TaskViewModel } from '@trinnvis/dabih-api-client';
import type { StartTaskInstance } from './start-task-instance';
import type { OrganizationState } from '../../types';
import { createSelector } from '@reduxjs/toolkit';
import { mondayThisWeek } from '../calendar/monday-this-week';
import { getOrganization } from '../organization/getOrganization';
import { getOrganizationId } from '../organization/getOrganizationId';
import { tasksNotDeleted } from './tasks-not-deleted';
import { singleUserVersion } from '../organization/singleUserVersion';
import { currentEmployeeUuid } from '../employees/current-employee-uuid';
import { currentPageId } from '../path';
import { startTasks } from './start-tasks';
import { getStartTaskInstances } from './get-start-task-instances';
import { functionsNotDeletedByUuid } from './functions-not-deleted-by-uuid';
import { employeeNamesByUuid } from '../employees/employee-names-by-uuid';
import { eventOccurrenceByTaskUuid } from '../events/event-occurrence-by-task-uuid';

function _isPageStartTask(task: TaskViewModel, pageId: number, organization: OrganizationState): boolean {
  if (!task || !pageId || !organization) {
    return false;
  }
  const assignedFunctionPages = organization.functionsById[task.functionUuid].pages;
  return assignedFunctionPages.includes(pageId) || (task.pages !== undefined && task.pages.includes(pageId));
}

export const startTasksForCurrentPage = createSelector(
  getOrganization,
  getOrganizationId,
  tasksNotDeleted,
  singleUserVersion,
  functionsNotDeletedByUuid,
  currentEmployeeUuid,
  employeeNamesByUuid,
  eventOccurrenceByTaskUuid,
  mondayThisWeek,
  currentPageId,
  startTasks,
  (
    organization,
    organizationId,
    tasks,
    singleUser,
    functionByUuid,
    currentEmployeeUuid,
    employeeNameByUuid,
    eventByTask,
    mondayThisWeek,
    pageId,
    startTaskInstances,
  ) => {
    if (organization === undefined || pageId === undefined) {
      return [];
    }
    const instanceByTaskUuid = new Map<string, StartTaskInstance>();
    for (const instance of startTaskInstances) {
      if (!instanceByTaskUuid.has(instance.uuid)) {
        instanceByTaskUuid.set(instance.uuid, instance);
      }
    }
    const startTaskDate = (task: TaskViewModel): string => {
      if (task.locked) {
        return instanceByTaskUuid.get(task.uuid)?.time.toString() ?? '';
      }
      return '';
    };
    const t = tasks
      .filter(function (task) {
        return _isPageStartTask(task, pageId, organization) && (task.locked || task.lastExecuted);
      })
      .sort(function (a, b) {
        const dateA = startTaskDate(a);
        const dateB = startTaskDate(b);
        return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
      });
    return getStartTaskInstances(
      organizationId,
      t,
      singleUser,
      functionByUuid,
      currentEmployeeUuid,
      employeeNameByUuid,
      eventByTask,
      mondayThisWeek,
    );
  },
);
